import type { Pharmacy } from '@quds/shared';
import type { FieldIssue } from '@/lib/api';

export interface PharmacyInfoValues {
  name: string;
  country: string;
  administrativeAreaLevel1: string;
  city: string;
  area: string;
  address: string;
  contactPerson: string;
  phone: string;
  email: string;
  notes: string;
}

export type FieldErrors<T> = Partial<Record<keyof T, string>>;

export const EMPTY_PHARMACY_INFO: PharmacyInfoValues = {
  name: '',
  country: '',
  administrativeAreaLevel1: '',
  city: '',
  area: '',
  address: '',
  contactPerson: '',
  phone: '',
  email: '',
  notes: '',
};

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE = /^\+?[0-9][0-9\s-]{6,19}$/;

/** Client-side checks mirror the API's zod rules; the API remains the authority. */
export function validatePharmacyInfo(v: PharmacyInfoValues): FieldErrors<PharmacyInfoValues> {
  const e: FieldErrors<PharmacyInfoValues> = {};
  if (v.name.trim().length < 2) e.name = 'Enter the pharmacy name (at least 2 characters).';
  else if (v.name.trim().length > 120) e.name = 'Pharmacy name must be 120 characters or fewer.';
  if (!v.country) e.country = 'Select a country.';
  if (!v.city.trim()) e.city = 'Enter the city.';
  if (v.address.trim().length > 255) e.address = 'Address must be 255 characters or fewer.';
  if (!v.contactPerson.trim()) e.contactPerson = 'Enter a contact person.';
  if (!v.phone.trim()) e.phone = 'Enter a phone number.';
  else if (!PHONE.test(v.phone.trim())) e.phone = 'Enter a valid phone number, e.g. +962 79 123 4567.';
  if (!v.email.trim()) e.email = 'Enter an email address.';
  else if (!EMAIL.test(v.email.trim())) e.email = 'Enter a valid email address.';
  if (v.notes.length > 1000) e.notes = 'Notes must be 1,000 characters or fewer.';
  return e;
}

export function hasErrors(errors: object): boolean {
  return Object.values(errors).some(Boolean);
}

export function infoValuesFrom(p: Pharmacy): PharmacyInfoValues {
  return {
    name: p.name,
    country: p.country,
    administrativeAreaLevel1: p.administrativeAreaLevel1 ?? '',
    city: p.city,
    area: p.area ?? '',
    address: p.address ?? '',
    contactPerson: p.contactPerson,
    phone: p.phone,
    email: p.email ?? '',
    notes: p.notes ?? '',
  };
}

export function toInfoInput(v: PharmacyInfoValues) {
  return {
    name: v.name.trim(),
    country: v.country,
    administrativeAreaLevel1: v.administrativeAreaLevel1.trim() || null,
    city: v.city.trim(),
    area: v.area.trim() || null,
    address: v.address.trim() || null,
    contactPerson: v.contactPerson.trim(),
    phone: v.phone.trim(),
    email: v.email.trim(),
    notes: v.notes.trim() || null,
  };
}

/** Maps API field issues (e.g. `branch.referenceCode`) onto form fields. */
export function mapFieldIssues<T extends object>(issues: FieldIssue[], map: Record<string, keyof T>): FieldErrors<T> {
  const out: FieldErrors<T> = {};
  for (const issue of issues) {
    const key = map[issue.path] ?? (issue.path as keyof T);
    if (!out[key]) out[key] = issue.message;
  }
  return out;
}
